/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { School } from './school'
import { SchoolReq } from './school-req'
import { BaseSchool } from './base-school'
import { LoadSchoolQuery } from './load-school-query'

const buildConfig = createConfigBuilder('growp')

export const schoolApi = {
  loadSchoolConfig(
    id: number,
    loadSchoolQuery?: LoadSchoolQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/school/schools/{id}',
      {
        id,
      },
      loadSchoolQuery
    )
  },

  /**
   * @summary 前台-学校单个查询
   *  - 取消 token 验证
   * @param id
   * @param [loadSchoolQuery]
   * @param [loadSchoolQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<School>
   */
  loadSchool<TQuery extends LoadSchoolQuery>(
    id: number,
    loadSchoolQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<School, TQuery['expand']>> {
    return axios(schoolApi.loadSchoolConfig(id, loadSchoolQuery, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new School(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useLoadSchool<TQuery extends LoadSchoolQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<School, TQuery['expand']>> {
    return useQueryApi<Expand<School, TQuery['expand']>>(
      schoolApi.loadSchool,
      'School.loadSchool',
      queryOptions,
      id,
      query
    )
  },

  updateSchoolConfig(
    id: number,
    schoolReq: SchoolReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/school/schools/{id}',
      {
        id,
      },
      null,
      schoolReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          name: (v: any) => v || '',
          identity: (v: any) => v || '',
          url: (v: any) => v || '',
          systemVer: (v: any) => v || null,
          status: (v: any) => v || null,
          hidden: (v: any) => v || false,
          expiredAt: (v: any) => v || '',
          description: (v: any) => v || '',
          logo: (v: any) => v || '',
          bgImage: (v: any) => v || '',
          themeColor: (v: any) => v || '',
          openMsgCheck: (v: any) => v || false,
          manager: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 前台-学校单个编辑
   * @param id
   * @param schoolReq
   * @param [options]
   * @return AxiosResponsePromise<School>
   */
  updateSchool(
    id: number,
    schoolReq: SchoolReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<School> {
    return axios(schoolApi.updateSchoolConfig(id, schoolReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new School(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useUpdateSchool(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: SchoolReq }) =>
        schoolApi.updateSchool(id, payload),
      mutationOptions
    )
  },

  checkSchoolConfig(
    url: string,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/school/schools/{url}/check', {
      url,
    })
  },

  /**
   * @summary 前台-根据域名获取单个学校
   * @param url 学校的域名
   * @param [options]
   * @return AxiosResponsePromise<BaseSchool | null>
   */
  checkSchool(
    url: string,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<BaseSchool | null> {
    return axios(schoolApi.checkSchoolConfig(url, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new BaseSchool(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCheckSchool(
    url: string,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<BaseSchool | null> {
    return useQueryApi<BaseSchool | null>(
      schoolApi.checkSchool,
      'School.checkSchool',
      queryOptions,
      url
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(schoolApi).forEach((api) => {
  // @ts-ignore
  schoolApi[api]._name_ = `School.${api}`
})
